import React, { useState } from 'react';
import ControlledDeliveryCompletion from './ControlledDeliveryCompletion';
import { post, put } from '../utils/api';

const METHODS = [
  ['fez', 'Fez Logistics'],
  ['public_transport', 'Public Transport'],
  ['same_city_rider', 'Same-City Rider'],
  ['customer_arranged_rider', 'Customer-Arranged Rider'],
  ['pickup', 'Customer Pickup']
];

const FEE_ARRANGEMENTS = [
  [
    'customer_pays_rider',
    'Customer pays rider directly'
  ],
  [
    'customer_paid_bloans_separately',
    'Customer paid Bloans separately'
  ],
  [
    'included_in_invoice',
    'Delivery fee included in invoice'
  ],
  [
    'bloans_pays_rider',
    'Bloans pays rider'
  ],
  [
    'customer_pickup_no_fee',
    'Customer pickup — no fee'
  ],
  [
    'customer_arranged_rider',
    'Customer arranged and pays rider'
  ],
  [
    'complimentary_md_approved',
    'Complimentary delivery — MD approved'
  ]
];

const AVAILABILITY_OPTIONS = [
  ['confirmed', 'Customer available'],
  ['unreachable', 'Customer unreachable'],
  ['unavailable', 'Customer unavailable'],
  [
    'reschedule_requested',
    'Customer requested rescheduling'
  ]
];

const fieldStyle = {
  marginBottom:12
};

const panelStyle = {
  background:'#fff',
  border:'1px solid #E8E0D4',
  borderRadius:12,
  padding:16,
  marginBottom:12
};

const stepBadge = complete => ({
  display:'inline-block',
  padding:'4px 9px',
  borderRadius:20,
  fontSize:10,
  fontWeight:700,
  background:complete ? '#E8F5EB' : '#FFF3DC',
  color:complete ? '#2E6B3A' : '#8B5E0A'
});

const initialPreparation = delivery => ({
  delivery_type:
    delivery.delivery_type === 'pending_arrangement'
      ? ''
      : delivery.delivery_type || '',
  delivery_address:
    delivery.delivery_address || '',
  recipient_name:
    delivery.recipient_name || '',
  recipient_phone:
    delivery.recipient_phone || '',
  recipient_email:
    delivery.recipient_email || '',
  recipient_address:
    delivery.recipient_address || '',
  is_dropship:
    delivery.is_dropship === true,
  neutral_packaging:
    delivery.neutral_packaging === true,
  include_priced_invoice:
    delivery.include_priced_invoice !== false,
  branding_permission:
    delivery.branding_permission === true,
  recipient_contact_permission:
    delivery.recipient_contact_permission === true,
  tracking_recipient:
    delivery.tracking_recipient || 'buyer',
  delivery_fee_arrangement:
    delivery.delivery_fee_arrangement || '',
  delivery_fee:
    delivery.delivery_fee || '',
  driver_amount:
    delivery.driver_amount || '',
  rider_name:
    delivery.rider_name || '',
  rider_phone:
    delivery.rider_phone || '',
  scheduled_delivery_at:
    delivery.scheduled_delivery_at
      ? String(delivery.scheduled_delivery_at)
          .slice(0,16)
      : '',
  notes:
    delivery.notes || ''
});

export default function ControlledDeliveryWorkflow({
  delivery,
  user,
  onRefresh
}) {
  const role = user?.role;

  const canFrontDesk = [
    'super_admin',
    'front_desk_officer',
    'procurement_logistics_officer'
  ].includes(role);

  const canInventory = [
    'super_admin',
    'inventory_officer'
  ].includes(role);

  const [busy, setBusy] = useState(false);
  const [openStep, setOpenStep] =
    useState(
      delivery.arrangement_confirmed
        ? ''
        : 'preparation'
    );

  const [preparation, setPreparation] =
    useState(
      initialPreparation(delivery)
    );

  const [availability, setAvailability] =
    useState({
      availability_status:
        delivery.customer_availability_status ===
          'pending'
          ? ''
          : delivery.customer_availability_status ||
            '',
      notes:'',
      scheduled_delivery_at:
        delivery.scheduled_delivery_at
          ? String(delivery.scheduled_delivery_at)
              .slice(0,16)
          : ''
    });

  const [handoverRequest, setHandoverRequest] =
    useState({
      package_condition:'good',
      inventory_notes:''
    });

  const [handoverAccept, setHandoverAccept] =
    useState({
      pin:'',
      acceptance_notes:''
    });

  const [dispatch, setDispatch] =
    useState({
      logistics_company:'',
      driver_name:
        delivery.rider_name || '',
      driver_phone:
        delivery.rider_phone || '',
      tracking_number:
        delivery.fez_tracking_code || '',
      dispatch_notes:'',
      expected_delivery:''
    });

  const [overrideReason, setOverrideReason] =
    useState('');

  const refresh = async message => {
    if (message) alert(message);

    if (onRefresh) {
      await onRefresh();
    }
  };

  const [actionError, setActionError] = useState('');

  const run = async action => {
    setActionError('');
    setBusy(true);

    try {
      await action();
    } catch (error) {
      setActionError(error.message || 'The delivery action could not be completed.');
    } finally {
      setBusy(false);
    }
  };

  const savePreparation = () =>
    run(async () => {
      if (!preparation.delivery_type) {
        alert('Select the actual delivery method.');
        return;
      }

      if (
        !preparation.delivery_fee_arrangement
      ) {
        alert(
          'Select how the delivery fee will be handled.'
        );
        return;
      }

      if (
        preparation.is_dropship &&
        (
          !preparation.recipient_name.trim() ||
          !preparation.recipient_phone.trim() ||
          !(
            preparation.recipient_address ||
            preparation.delivery_address
          ).trim()
        )
      ) {
        alert(
          'Dropship delivery requires the recipient name, phone number and address.'
        );
        return;
      }

      if (
        preparation.is_dropship &&
        !preparation.recipient_contact_permission
      ) {
        alert(
          'Confirm that the buyer permits Bloans to contact the recipient.'
        );
        return;
      }

      const deliveryFee =
        String(
          preparation.delivery_fee ?? ''
        ).trim() === ''
          ? null
          : Number(
              preparation.delivery_fee
            );

      const driverAmount =
        String(
          preparation.driver_amount ?? ''
        ).trim() === ''
          ? null
          : Number(
              preparation.driver_amount
            );

      if (
        deliveryFee !== null &&
        (
          !Number.isFinite(deliveryFee) ||
          deliveryFee < 0
        )
      ) {
        alert(
          'Delivery fee must be a valid non-negative amount.'
        );
        return;
      }

      if (
        driverAmount !== null &&
        (
          !Number.isFinite(driverAmount) ||
          driverAmount < 0
        )
      ) {
        alert(
          'Amount payable to the rider must be a valid non-negative amount.'
        );
        return;
      }

      const response = await put(
        '/deliveries/' +
          delivery.id +
          '/preparation',
        {
          ...preparation,
          delivery_fee:
            deliveryFee,
          driver_amount:
            driverAmount,
          scheduled_delivery_at:
            preparation
              .scheduled_delivery_at ||
            null
        }
      );

      setOpenStep('availability');

      await refresh(
        response.message ||
        'Delivery arrangement confirmed.'
      );
    });

  const saveAvailability = () =>
    run(async () => {
      if (!availability.availability_status) {
        alert(
          'Select the customer availability result.'
        );
        return;
      }

      if (
        availability.availability_status ===
          'reschedule_requested' &&
        !availability.scheduled_delivery_at
      ) {
        alert(
          'Enter the new delivery date and time.'
        );
        return;
      }

      const response = await post(
        '/deliveries/' +
          delivery.id +
          '/availability',
        availability
      );

      setOpenStep('');

      await refresh(
        response.message ||
        'Customer availability updated.'
      );
    });

  const requestHandover = () =>
    run(async () => {
      if (
        delivery.customer_availability_status !==
          'confirmed'
      ) {
        alert(
          'Front Desk must confirm customer availability before Inventory issues the package.'
        );
        return;
      }

      const response = await post(
        '/deliveries/' +
          delivery.id +
          '/handover/request',
        handoverRequest
      );

      await refresh(
        response.message ||
        'Package handover requested.'
      );
    });

  const acceptHandover = () =>
    run(async () => {
      if (!/^\d{4}$/.test(handoverAccept.pin)) {
        alert(
          'Enter your four-digit staff PIN.'
        );
        return;
      }

      const response = await post(
        '/deliveries/' +
          delivery.id +
          '/handover/accept',
        handoverAccept
      );

      setHandoverAccept({
        pin:'',
        acceptance_notes:''
      });

      await refresh(
        response.message ||
        'Package accepted from Inventory.'
      );
    });

  const dispatchDelivery = () =>
    run(async () => {
      const pickup =
        delivery.delivery_type === 'pickup';

      if (
        !pickup &&
        (
          !dispatch.logistics_company.trim() ||
          !dispatch.driver_name.trim()
        )
      ) {
        alert(
          'Enter the logistics company and rider or driver name.'
        );
        return;
      }

      const response = await post(
        '/deliveries/' +
          delivery.id +
          '/dispatch',
        dispatch
      );

      await refresh(
        response.message ||
        'Delivery dispatched successfully.'
      );
    });

  const requestOverride = () =>
    run(async () => {
      if (overrideReason.trim().length < 10) {
        alert(
          'Enter a clear override reason of at least 10 characters.'
        );
        return;
      }

      const response = await post(
        '/deliveries/' +
          delivery.id +
          '/dispatch-override',
        {
          ...dispatch,
          override_reason:
            overrideReason.trim()
        }
      );

      await refresh(
        response.message ||
        'Dispatch exception submitted for Managing Director approval.'
      );
    });

  const arrangementDone =
    delivery.arrangement_confirmed === true;

  const availabilityDone =
    delivery.customer_availability_status ===
      'confirmed';

  const handoverRequested =
    ['requested', 'accepted']
      .includes(delivery.handover_status);

  const handoverAccepted =
    delivery.handover_status === 'accepted';

  const dispatched =
    [
      'dispatched',
      'out_for_delivery',
      'delivery_attempt_failed',
      'awaiting_proof',
      'awaiting_completion',
      'ready_for_pickup',
      'delivered',
      'picked_up'
    ].includes(delivery.status);

  const updatePreparation = (key, value) =>
    setPreparation(current => ({
      ...current,
      [key]:value
    }));

  return (
    <div style={{
      background:'#F9F8F5',
      border:'1px solid #D8CFBF',
      borderRadius:12,
      padding:16,
      marginBottom:16
    }}>
      {actionError && (
        <div role="alert" style={{ background:'#FFF0ED', color:'#8F2E24', border:'1px solid #8F2E24', padding:14, marginBottom:14, fontWeight:700 }}>
          {actionError}
        </div>
      )}
      <div style={{
        display:'flex',
        justifyContent:'space-between',
        gap:12,
        flexWrap:'wrap',
        marginBottom:14
      }}>
        <div>
          <div style={{
            fontSize:13,
            fontWeight:800,
            color:'#3B2A21'
          }}>
            Controlled Delivery Workflow
          </div>

          <div style={{
            fontSize:11,
            color:'#8A8A8A',
            marginTop:3
          }}>
            Follow each stage in order. Completed
            stages cannot be bypassed.
          </div>
        </div>

        <span style={stepBadge(dispatched)}>
          {delivery.status
            ?.replace(/_/g,' ') ||
            'Pending'}
        </span>
      </div>

      <div style={{
        display:'flex',
        gap:6,
        flexWrap:'wrap',
        marginBottom:16
      }}>
        <span style={stepBadge(arrangementDone)}>
          {arrangementDone ? '✓ ' : ''}
          1. Arrangement
        </span>

        <span style={stepBadge(availabilityDone)}>
          {availabilityDone ? '✓ ' : ''}
          2. Availability
        </span>

        <span style={stepBadge(handoverRequested)}>
          {handoverRequested ? '✓ ' : ''}
          3. Inventory Issue
        </span>

        <span style={stepBadge(handoverAccepted)}>
          {handoverAccepted ? '✓ ' : ''}
          4. Front Desk Acceptance
        </span>

        <span style={stepBadge(dispatched)}>
          {dispatched ? '✓ ' : ''}
          5. Dispatch
        </span>
      </div>

      {!dispatched && canFrontDesk && (
        <div style={panelStyle}>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() =>
              setOpenStep(
                openStep === 'preparation'
                  ? ''
                  : 'preparation'
              )
            }
          >
            {arrangementDone
              ? 'Review Delivery Arrangement'
              : '1. Confirm Delivery Arrangement'}
          </button>

          {openStep === 'preparation' && (
            <div style={{marginTop:14}}>
              <div className="form-row">
                <div
                  className="form-group"
                  style={fieldStyle}
                >
                  <label className="form-label">
                    Delivery Method *
                  </label>

                  <select
                    className="form-control"
                    value={
                      preparation.delivery_type
                    }
                    onChange={event =>
                      updatePreparation(
                        'delivery_type',
                        event.target.value
                      )
                    }
                  >
                    <option value="">
                      Select method
                    </option>

                    {METHODS.map(
                      ([value, label]) => (
                        <option
                          key={value}
                          value={value}
                        >
                          {label}
                        </option>
                      )
                    )}
                  </select>
                </div>

                <div
                  className="form-group"
                  style={fieldStyle}
                >
                  <label className="form-label">
                    Fee Arrangement *
                  </label>

                  <select
                    className="form-control"
                    value={
                      preparation
                        .delivery_fee_arrangement
                    }
                    onChange={event =>
                      updatePreparation(
                        'delivery_fee_arrangement',
                        event.target.value
                      )
                    }
                  >
                    <option value="">
                      Select arrangement
                    </option>

                    {FEE_ARRANGEMENTS.map(
                      ([value, label]) => (
                        <option
                          key={value}
                          value={value}
                        >
                          {label}
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Delivery Address
                </label>

                <textarea
                  className="form-control"
                  rows={2}
                  value={
                    preparation.delivery_address
                  }
                  onChange={event =>
                    updatePreparation(
                      'delivery_address',
                      event.target.value
                    )
                  }
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Delivery Fee
                  </label>

                  <input
                    type="number"
                    className="form-control"
                    value={
                      preparation.delivery_fee
                    }
                    onChange={event =>
                      updatePreparation(
                        'delivery_fee',
                        event.target.value
                      )
                    }
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Amount Payable to Rider
                  </label>

                  <input
                    type="number"
                    className="form-control"
                    value={
                      preparation.driver_amount
                    }
                    onChange={event =>
                      updatePreparation(
                        'driver_amount',
                        event.target.value
                      )
                    }
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Rider/Driver Name
                  </label>

                  <input
                    className="form-control"
                    value={
                      preparation.rider_name
                    }
                    onChange={event =>
                      updatePreparation(
                        'rider_name',
                        event.target.value
                      )
                    }
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Rider/Driver Phone
                  </label>

                  <input
                    className="form-control"
                    value={
                      preparation.rider_phone
                    }
                    onChange={event =>
                      updatePreparation(
                        'rider_phone',
                        event.target.value
                      )
                    }
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Scheduled Delivery Date/Time
                </label>

                <input
                  type="datetime-local"
                  className="form-control"
                  value={
                    preparation
                      .scheduled_delivery_at
                  }
                  onChange={event =>
                    updatePreparation(
                      'scheduled_delivery_at',
                      event.target.value
                    )
                  }
                />
              </div>

              <label style={{
                display:'flex',
                gap:8,
                marginBottom:12,
                fontSize:13
              }}>
                <input
                  type="checkbox"
                  checked={
                    preparation.is_dropship
                  }
                  onChange={event =>
                    updatePreparation(
                      'is_dropship',
                      event.target.checked
                    )
                  }
                />
                This is a dropship delivery
              </label>

              {preparation.is_dropship && (
                <div style={{
                  background:'#FFF3DC',
                  borderRadius:8,
                  padding:12,
                  marginBottom:12
                }}>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        Recipient Name *
                      </label>

                      <input
                        className="form-control"
                        value={
                          preparation.recipient_name
                        }
                        onChange={event =>
                          updatePreparation(
                            'recipient_name',
                            event.target.value
                          )
                        }
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Recipient Phone *
                      </label>

                      <input
                        className="form-control"
                        value={
                          preparation.recipient_phone
                        }
                        onChange={event =>
                          updatePreparation(
                            'recipient_phone',
                            event.target.value
                          )
                        }
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Recipient Email
                    </label>

                    <input
                      type="email"
                      className="form-control"
                      value={
                        preparation.recipient_email
                      }
                      onChange={event =>
                        updatePreparation(
                          'recipient_email',
                          event.target.value
                        )
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Recipient Address *
                    </label>

                    <textarea
                      className="form-control"
                      rows={2}
                      value={
                        preparation.recipient_address
                      }
                      onChange={event =>
                        updatePreparation(
                          'recipient_address',
                          event.target.value
                        )
                      }
                    />
                  </div>

                  <label style={{
                    display:'flex',
                    gap:8,
                    marginBottom:8,
                    fontSize:13
                  }}>
                    <input
                      type="checkbox"
                      checked={
                        preparation
                          .recipient_contact_permission
                      }
                      onChange={event =>
                        updatePreparation(
                          'recipient_contact_permission',
                          event.target.checked
                        )
                      }
                    />
                    Buyer permits Bloans to contact
                    the recipient
                  </label>

                  <label style={{
                    display:'flex',
                    gap:8,
                    marginBottom:8,
                    fontSize:13
                  }}>
                    <input
                      type="checkbox"
                      checked={
                        preparation.neutral_packaging
                      }
                      onChange={event =>
                        updatePreparation(
                          'neutral_packaging',
                          event.target.checked
                        )
                      }
                    />
                    Use neutral packaging
                  </label>

                  <label style={{
                    display:'flex',
                    gap:8,
                    marginBottom:8,
                    fontSize:13
                  }}>
                    <input
                      type="checkbox"
                      checked={
                        preparation.include_priced_invoice
                      }
                      onChange={event =>
                        updatePreparation(
                          'include_priced_invoice',
                          event.target.checked
                        )
                      }
                    />
                    Include priced invoice
                  </label>

                  <label style={{
                    display:'flex',
                    gap:8,
                    marginBottom:8,
                    fontSize:13
                  }}>
                    <input
                      type="checkbox"
                      checked={
                        preparation.branding_permission
                      }
                      onChange={event =>
                        updatePreparation(
                          'branding_permission',
                          event.target.checked
                        )
                      }
                    />
                    Buyer permits Bloans branding
                  </label>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">
                  Send Tracking To
                </label>

                <select
                  className="form-control"
                  value={
                    preparation.tracking_recipient
                  }
                  onChange={event =>
                    updatePreparation(
                      'tracking_recipient',
                      event.target.value
                    )
                  }
                >
                  <option value="buyer">
                    Buyer only
                  </option>

                  <option value="recipient">
                    Recipient only
                  </option>

                  <option value="both">
                    Buyer and recipient
                  </option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Internal Notes
                </label>

                <textarea
                  className="form-control"
                  rows={2}
                  value={preparation.notes}
                  onChange={event =>
                    updatePreparation(
                      'notes',
                      event.target.value
                    )
                  }
                />
              </div>

              <button
                type="button"
                className="btn btn-primary"
                onClick={savePreparation}
                disabled={busy}
              >
                {busy
                  ? 'Saving...'
                  : 'Save Arrangement'}
              </button>
            </div>
          )}
        </div>
      )}

      {!dispatched && canFrontDesk && (
        <div style={panelStyle}>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() =>
              setOpenStep(
                openStep === 'availability'
                  ? ''
                  : 'availability'
              )
            }
          >
            2. Record Customer Availability
          </button>

          {openStep === 'availability' && (
            <div style={{marginTop:14}}>
              <div className="form-group">
                <label className="form-label">
                  Contact Result *
                </label>

                <select
                  className="form-control"
                  value={
                    availability
                      .availability_status
                  }
                  onChange={event =>
                    setAvailability(current => ({
                      ...current,
                      availability_status:
                        event.target.value
                    }))
                  }
                >
                  <option value="">
                    Select result
                  </option>

                  {AVAILABILITY_OPTIONS.map(
                    ([value, label]) => (
                      <option
                        key={value}
                        value={value}
                      >
                        {label}
                      </option>
                    )
                  )}
                </select>
              </div>

              {availability.availability_status ===
                'reschedule_requested' && (
                <div className="form-group">
                  <label className="form-label">
                    New Delivery Date/Time *
                  </label>

                  <input
                    type="datetime-local"
                    className="form-control"
                    value={
                      availability
                        .scheduled_delivery_at
                    }
                    onChange={event =>
                      setAvailability(current => ({
                        ...current,
                        scheduled_delivery_at:
                          event.target.value
                      }))
                    }
                  />
                </div>
              )}

              <div className="form-group">
                <label className="form-label">
                  Call/Contact Notes
                </label>

                <textarea
                  className="form-control"
                  rows={2}
                  value={availability.notes}
                  onChange={event =>
                    setAvailability(current => ({
                      ...current,
                      notes:event.target.value
                    }))
                  }
                  placeholder="Record call result or customer instruction."
                />
              </div>

              <button
                type="button"
                className="btn btn-primary"
                onClick={saveAvailability}
                disabled={busy}
              >
                {busy
                  ? 'Saving...'
                  : 'Save Availability'}
              </button>
            </div>
          )}
        </div>
      )}

      {!dispatched &&
        canInventory &&
        availabilityDone &&
        !handoverRequested && (
        <div style={panelStyle}>
          <div style={{
            fontSize:12,
            fontWeight:700,
            color:'#3B2A21',
            marginBottom:10
          }}>
            3. Inventory Issues Package
          </div>

          <div className="form-group">
            <label className="form-label">
              Package Condition
            </label>

            <select
              className="form-control"
              value={
                handoverRequest.package_condition
              }
              onChange={event =>
                setHandoverRequest(current => ({
                  ...current,
                  package_condition:
                    event.target.value
                }))
              }
            >
              <option value="good">
                Good and sealed
              </option>

              <option value="damaged">
                Damaged — do not issue
              </option>

              <option value="incomplete">
                Incomplete — do not issue
              </option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">
              Inventory Notes
            </label>

            <textarea
              className="form-control"
              rows={2}
              value={
                handoverRequest.inventory_notes
              }
              onChange={event =>
                setHandoverRequest(current => ({
                  ...current,
                  inventory_notes:
                    event.target.value
                }))
              }
            />
          </div>

          <button
            type="button"
            className="btn btn-primary"
            onClick={requestHandover}
            disabled={
              busy ||
              handoverRequest.package_condition !==
                'good'
            }
          >
            {busy
              ? 'Issuing...'
              : 'Issue Package to Front Desk'}
          </button>
        </div>
      )}

      {!dispatched &&
        canFrontDesk &&
        handoverRequested &&
        !handoverAccepted && (
        <div style={panelStyle}>
          <div style={{
            fontSize:12,
            fontWeight:700,
            color:'#3B2A21',
            marginBottom:10
          }}>
            4. Accept Package from Inventory
          </div>

          <div style={{
            fontSize:11,
            color:'#8A8A8A',
            marginBottom:10
          }}>
            Physically check the package before
            entering your four-digit PIN.
          </div>

          <div className="form-group">
            <label className="form-label">
              Staff PIN *
            </label>

            <input
              type="password"
              inputMode="numeric"
              maxLength={4}
              className="form-control"
              value={handoverAccept.pin}
              onChange={event =>
                setHandoverAccept(current => ({
                  ...current,
                  pin:event.target.value
                    .replace(/\D/g,'')
                    .slice(0,4)
                }))
              }
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              Acceptance Notes
            </label>

            <textarea
              className="form-control"
              rows={2}
              value={
                handoverAccept.acceptance_notes
              }
              onChange={event =>
                setHandoverAccept(current => ({
                  ...current,
                  acceptance_notes:
                    event.target.value
                }))
              }
            />
          </div>

          <button
            type="button"
            className="btn btn-primary"
            onClick={acceptHandover}
            disabled={busy}
          >
            {busy
              ? 'Accepting...'
              : 'Accept Package'}
          </button>
        </div>
      )}

      {!dispatched &&
        canFrontDesk &&
        handoverAccepted &&
        arrangementDone &&
        availabilityDone && (
        <div style={panelStyle}>
          <div style={{
            fontSize:12,
            fontWeight:700,
            color:'#3B2A21',
            marginBottom:10
          }}>
            5. Dispatch or Prepare Pickup
          </div>

          {delivery.delivery_type !== 'pickup' && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Logistics Company *
                  </label>

                  <input
                    className="form-control"
                    value={
                      dispatch.logistics_company
                    }
                    onChange={event =>
                      setDispatch(current => ({
                        ...current,
                        logistics_company:
                          event.target.value
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Rider/Driver Name *
                  </label>

                  <input
                    className="form-control"
                    value={dispatch.driver_name}
                    onChange={event =>
                      setDispatch(current => ({
                        ...current,
                        driver_name:
                          event.target.value
                      }))
                    }
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Rider/Driver Phone
                  </label>

                  <input
                    className="form-control"
                    value={dispatch.driver_phone}
                    onChange={event =>
                      setDispatch(current => ({
                        ...current,
                        driver_phone:
                          event.target.value
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Provider Tracking Number
                  </label>

                  <input
                    className="form-control"
                    value={
                      dispatch.tracking_number
                    }
                    onChange={event =>
                      setDispatch(current => ({
                        ...current,
                        tracking_number:
                          event.target.value
                      }))
                    }
                  />
                </div>
              </div>
            </>
          )}

          <div className="form-group">
            <label className="form-label">
              Expected Delivery Date
            </label>

            <input
              type="date"
              className="form-control"
              value={dispatch.expected_delivery}
              onChange={event =>
                setDispatch(current => ({
                  ...current,
                  expected_delivery:
                    event.target.value
                }))
              }
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              Dispatch Notes
            </label>

            <textarea
              className="form-control"
              rows={2}
              value={dispatch.dispatch_notes}
              onChange={event =>
                setDispatch(current => ({
                  ...current,
                  dispatch_notes:
                    event.target.value
                }))
              }
            />
          </div>

          <button
            type="button"
            className="btn btn-primary"
            onClick={dispatchDelivery}
            disabled={busy}
          >
            {busy
              ? 'Processing...'
              : delivery.delivery_type === 'pickup'
                ? 'Mark Ready for Pickup'
                : 'Dispatch Package'}
          </button>

          <div style={{
            borderTop:'1px solid #E8E0D4',
            marginTop:16,
            paddingTop:14
          }}>
            <div style={{
              fontSize:11,
              fontWeight:700,
              color:'#8F2E24',
              marginBottom:6
            }}>
              Outstanding product balance
            </div>

            <div style={{
              fontSize:11,
              color:'#8A8A8A',
              marginBottom:8
            }}>
              Use only if dispatch was blocked for
              unpaid product balance. The Managing
              Director must approve the request.
            </div>

            <textarea
              className="form-control"
              rows={2}
              value={overrideReason}
              onChange={event =>
                setOverrideReason(
                  event.target.value
                )
              }
              placeholder="Explain why dispatch is requested before full payment."
            />

            <button
              type="button"
              className="btn btn-outline btn-sm"
              style={{
                marginTop:8,
                color:'#8F2E24',
                borderColor:'#8F2E24'
              }}
              onClick={requestOverride}
              disabled={busy}
            >
              Request MD Dispatch Override
            </button>
          </div>
        </div>
      )}

      {dispatched && (
        <ControlledDeliveryCompletion
          delivery={delivery}
          onRefresh={onRefresh}
        />
      )}
    </div>
  );
}
